import { useEffect, useState } from "react";
import {
  fetchTrainerContacts,
  createTrainerContact,
  updateTrainerContact,
  deleteTrainerContact,
} from "@/api/trainerAuth";

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    title: "",
    url: "",
    icon: "",
    is_active: true,
  });

  // تحميل روابط التواصل
  const loadContacts = async () => {
    try {
      const res = await fetchTrainerContacts();

      if (Array.isArray(res)) {
        setContacts(res);
      } else if (Array.isArray(res?.contacts)) {
        setContacts(res.contacts);
      } else {
        setContacts([]);
      }
    } catch (e) {
      console.error(e);
      setContacts([]);
    }
  };

  useEffect(() => {
    loadContacts();
  }, []);

  // إضافة / تحديث
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (editingId) {
        await updateTrainerContact(editingId, form);
        setSuccess("تم تحديث الرابط بنجاح");
      } else {
        await createTrainerContact(form);
        setSuccess("تم إضافة الرابط بنجاح");
      }

      resetForm();
      loadContacts();
    } catch (e) {
      console.error(e);
      setError("حدث خطأ أثناء الحفظ");
    } finally {
      setLoading(false);
    }
  };

  // حذف
  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الرابط؟")) return;
    
    try {
      await deleteTrainerContact(id);
      setSuccess("تم حذف الرابط بنجاح");
      loadContacts();
    } catch (e) {
      console.error(e);
      setError("حدث خطأ أثناء الحذف");
    }
  };

  // تعديل
  const handleEdit = (contact) => {
    setEditingId(contact.id);
    setForm({
      title: contact.title || "",
      url: contact.url || "",
      icon: contact.icon || "",
      is_active: Boolean(contact.is_active),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      title: "",
      url: "",
      icon: "",
      is_active: true,
    });
    setError("");
    setSuccess("");
  };

  // عرض الأيقونة
  const renderIcon = (iconName) => {
    const icons = {
      whatsapp: "💬",
      telegram: "📱",
      phone: "📞",
      email: "📧",
      instagram: "📸",
      facebook: "👥",
      twitter: "🐦",
      linkedin: "💼",
      website: "🌐",
      location: "📍",
      youtube: "🎬",
      tiktok: "🎵"
    };
    
    return icons[iconName] || "🔗";
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.pageTitle}>إدارة روابط التواصل</h1>
          <p style={styles.pageSubtitle}>أضف وعدّل روابط التواصل الخاصة بك لعملائك</p>
        </div>
        <div style={styles.headerIcon}>📱</div>
      </div>

      <div style={styles.contentWrapper}>
        {/* ===== FORM ===== */}
        <div style={styles.formCard}>
          <h2 style={styles.formTitle}>
            {editingId ? "تعديل رابط التواصل" : "إضافة رابط تواصل جديد"}
          </h2>
          
          {success && (
            <div style={styles.successMessage}>
              <span style={styles.successIcon}>✅</span>
              {success}
            </div>
          )}
          
          {error && (
            <div style={styles.errorMessage}>
              <span style={styles.errorIcon}>⚠️</span>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <span style={styles.required}>*</span> عنوان الرابط
                </label>
                <input
                  placeholder="مثال: واتساب، هاتف، إنستغرام..."
                  value={form.title}
                  onChange={(e) =>
                    setForm({ ...form, title: e.target.value })
                  }
                  required
                  style={styles.input}
                />
                <small style={styles.helpText}>اسم يظهر للعملاء</small>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <span style={styles.required}>*</span> رابط التواصل
                </label>
                <input
                  placeholder="https://wa.me/9665xxxxxxxx"
                  value={form.url}
                  onChange={(e) =>
                    setForm({ ...form, url: e.target.value })
                  }
                  required
                  style={styles.input}
                  dir="ltr"
                />
                <small style={styles.helpText}>يجب أن يبدأ بـ https://</small>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>الأيقونة</label>
                <div style={styles.iconSelect}>
                  <select
                    value={form.icon}
                    onChange={(e) =>
                      setForm({ ...form, icon: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="">اختر أيقونة</option>
                    <option value="whatsapp">واتساب</option>
                    <option value="telegram">تليجرام</option>
                    <option value="phone">هاتف</option>
                    <option value="email">بريد إلكتروني</option>
                    <option value="instagram">إنستغرام</option>
                    <option value="facebook">فيسبوك</option>
                    <option value="twitter">تويتر</option>
                    <option value="linkedin">لينكدإن</option>
                    <option value="website">موقع ويب</option>
                    <option value="location">موقع</option>
                    <option value="youtube">يوتيوب</option>
                    <option value="tiktok">تيك توك</option>
                  </select>
                  <span style={styles.iconPreview}>
                    {renderIcon(form.icon)}
                  </span>
                </div>
                <small style={styles.helpText}>اختر أيقونة معبّرة للرابط</small>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>حالة الرابط</label>
                <div style={styles.checkboxContainer}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) =>
                        setForm({ ...form, is_active: e.target.checked })
                      }
                      style={styles.checkbox}
                    />
                    <span style={styles.checkboxText}>
                      {form.is_active ? "نشط" : "غير نشط"}
                    </span>
                  </label>
                </div>
                <small style={styles.helpText}>
                  {form.is_active 
                    ? "الرابط ظاهر للعملاء" 
                    : "الرابط مخفي عن العملاء"}
                </small>
              </div>
            </div>

            <div style={styles.formActions}>
              <button 
                type="submit" 
                disabled={loading} 
                style={loading ? {...styles.submitButton, ...styles.submitButtonLoading} : styles.submitButton}
              >
                {loading ? (
                  <>
                    <span style={styles.spinner}></span>
                    جاري الحفظ...
                  </>
                ) : editingId ? (
                  "تحديث الرابط"
                ) : (
                  "إضافة رابط جديد"
                )}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  style={styles.cancelButton}
                >
                  إلغاء التعديل
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ===== LIST ===== */}
        <div style={styles.listSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>روابط التواصل الحالية</h2>
            <div style={styles.stats}>
              <span style={styles.stat}>
                <strong>{contacts.length}</strong> رابط
              </span>
              <span style={styles.stat}>
                <strong>{contacts.filter(c => c.is_active).length}</strong> نشط
              </span>
            </div>
          </div>

          {contacts.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📭</div>
              <h3 style={styles.emptyTitle}>لا توجد روابط تواصل بعد</h3>
              <p style={styles.emptyText}>ابدأ بإضافة أول رابط تواصل لك</p>
            </div>
          ) : (
            <div style={styles.contactsGrid}>
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  style={{
                    ...styles.contactCard,
                    opacity: contact.is_active ? 1 : 0.7,
                    borderLeft: contact.is_active ? '4px solid #4CAF50' : '4px solid #9E9E9E',
                  }}
                >
                  <div style={styles.contactHeader}>
                    <div style={styles.contactIcon}>
                      {renderIcon(contact.icon)}
                    </div>
                    <div>
                      <h3 style={styles.contactTitle}>{contact.title}</h3>
                      {!contact.is_active && (
                        <span style={styles.inactiveBadge}>غير نشط</span>
                      )}
                    </div>
                  </div>

                  <div style={styles.contactBody}>
                    <a 
                      href={contact.url} 
                      target="_blank" 
                      rel="noreferrer"
                      style={styles.contactLink}
                    >
                      {contact.url.length > 40 
                        ? contact.url.substring(0, 40) + '...' 
                        : contact.url}
                    </a>
                    
                    {contact.icon && (
                      <div style={styles.contactMeta}>
                        <span style={styles.metaItem}>
                          <span style={styles.metaIcon}>🎨</span>
                          {contact.icon}
                        </span>
                      </div>
                    )}
                  </div>

                  <div style={styles.contactActions}>
                    <button 
                      onClick={() => handleEdit(contact)}
                      style={styles.editButton}
                    >
                      تعديل
                    </button>

                    <button
                      onClick={() => handleDelete(contact.id)}
                      style={styles.deleteButton}
                    >
                      حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f7fa',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    direction: 'rtl',
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '40px 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerContent: {
    flex: 1,
  },
  pageTitle: {
    fontSize: '2rem',
    margin: '0 0 10px 0',
    fontWeight: '700',
  },
  pageSubtitle: {
    fontSize: '1rem',
    opacity: '0.9',
    margin: '0',
  },
  headerIcon: {
    fontSize: '3rem',
    background: 'rgba(255, 255, 255, 0.2)',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)',
  },
  contentWrapper: {
    maxWidth: '1200px',
    margin: '-30px auto 0',
    padding: '0 20px 40px',
    position: 'relative',
  },
  formCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    padding: '30px',
    marginBottom: '30px',
    animation: 'fadeIn 0.5s ease-out',
  },
  formTitle: {
    fontSize: '1.5rem',
    margin: '0 0 20px 0',
    color: '#333',
    fontWeight: '600',
  },
  form: {
    marginTop: '20px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  inputGroup: {
    marginBottom: '10px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#555',
    fontSize: '0.95rem',
  },
  required: {
    color: '#f44336',
    marginLeft: '4px',
  },
  input: {
    width: '100%',
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  helpText: {
    display: 'block',
    marginTop: '6px',
    color: '#777',
    fontSize: '0.85rem',
  },
  iconSelect: {
    position: 'relative',
  },
  select: {
    width: '100%',
    padding: '12px 40px 12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: 'white',
    appearance: 'none',
    fontFamily: 'inherit',
  },
  iconPreview: {
    position: 'absolute',
    left: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '1.2rem',
  },
  checkboxContainer: {
    marginTop: '5px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    accentColor: '#4CAF50',
  },
  checkboxText: {
    fontSize: '1rem',
    color: '#555',
  },
  formActions: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
  },
  submitButton: {
    padding: '14px 30px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: '180px',
  },
  submitButtonLoading: {
    opacity: '0.8',
    cursor: 'not-allowed',
  },
  spinner: {
    width: '18px',
    height: '18px',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '50%',
    borderTopColor: 'white',
    animation: 'spin 1s ease-in-out infinite',
  },
  cancelButton: {
    padding: '14px 25px',
    background: '#f5f5f5',
    color: '#666',
    border: '2px solid #ddd',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  successMessage: {
    background: '#e8f5e9',
    color: '#2e7d32',
    padding: '12px 20px',
    borderRadius: '8px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRight: '4px solid #4CAF50',
    animation: 'fadeIn 0.3s ease-out',
  },
  successIcon: {
    fontSize: '1.2rem',
  },
  errorMessage: {
    background: '#ffebee',
    color: '#c62828',
    padding: '12px 20px',
    borderRadius: '8px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRight: '4px solid #f44336',
    animation: 'fadeIn 0.3s ease-out',
  },
  errorIcon: {
    fontSize: '1.2rem',
  },
  listSection: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    padding: '30px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    margin: '0',
    color: '#333',
    fontWeight: '600',
  },
  stats: {
    display: 'flex',
    gap: '20px',
  },
  stat: {
    background: '#f5f7fa',
    padding: '8px 15px',
    borderRadius: '20px',
    fontSize: '0.9rem',
    color: '#666',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#777',
  },
  emptyIcon: {
    fontSize: '4rem',
    opacity: '0.5',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '1.5rem',
    margin: '0 0 10px 0',
    color: '#555',
  },
  emptyText: {
    fontSize: '1rem',
    opacity: '0.8',
  },
  contactsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '20px',
  },
  contactCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e8e8e8',
    transition: 'all 0.3s ease',
    animation: 'fadeIn 0.5s ease-out',
  },
  contactHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '15px',
  },
  contactIcon: {
    fontSize: '2rem',
    width: '60px',
    height: '60px',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactTitle: {
    margin: '0 0 5px 0',
    fontSize: '1.2rem',
    color: '#333',
    fontWeight: '600',
  },
  inactiveBadge: {
    background: '#ffebee',
    color: '#f44336',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  contactBody: {
    marginBottom: '20px',
  },
  contactLink: {
    display: 'block',
    color: '#667eea',
    textDecoration: 'none',
    marginBottom: '10px',
    fontSize: '0.9rem',
    padding: '10px',
    background: '#f8f9ff',
    borderRadius: '6px',
    border: '1px solid #e0e0ff',
    wordBreak: 'break-all',
  },
  contactMeta: {
    display: 'flex',
    gap: '15px',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '0.85rem',
    color: '#666',
  },
  metaIcon: {
    fontSize: '0.9rem',
  },
  contactActions: {
    display: 'flex',
    gap: '10px',
  },
  editButton: {
    flex: 1,
    padding: '10px 15px',
    background: '#e3f2fd',
    color: '#1976d2',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  deleteButton: {
    flex: 1,
    padding: '10px 15px',
    background: '#ffebee',
    color: '#f44336',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
};

// إضافة تأثيرات Hover
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const style = document.createElement('style');
    style.textContent = `
      .contact-card-hover:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
      }
      
      .input-focus:focus {
        outline: none;
        border-color: #667eea !important;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
      }
      
      .submit-btn-hover:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3) !important;
      }
      
      .cancel-btn-hover:hover {
        background: #e0e0e0 !important;
        border-color: #bbb !important;
      }
      
      .edit-btn-hover:hover {
        background: #bbdefb !important;
      }
      
      .delete-btn-hover:hover {
        background: #ffcdd2 !important;
      }
      
      .contact-link-hover:hover {
        background: #e8eaff !important;
        text-decoration: underline !important;
      }
      
      @media (max-width: 768px) {
        .form-actions-responsive {
          flex-direction: column;
          gap: 10px !important;
        }
        
        .contacts-grid-responsive {
          grid-template-columns: 1fr !important;
        }
        
        .section-header-responsive {
          flex-direction: column;
          gap: 15px;
          align-items: flex-start !important;
        }
      }
      
      @media (max-width: 480px) {
        .header-content-responsive {
          flex-direction: column;
          gap: 20px;
          text-align: center;
        }
        
        .form-grid-responsive {
          grid-template-columns: 1fr !important;
        }
      }
    `;
    document.head.appendChild(style);
  }, 100);
}

// إضافة classes للعناصر
const addHoverClasses = () => {
  if (typeof window !== 'undefined') {
    setTimeout(() => {
      document.querySelectorAll('[style*="contactCard"]').forEach(el => {
        el.className = (el.className || '') + ' contact-card-hover';
      });
      document.querySelectorAll('input').forEach(el => {
        el.className = (el.className || '') + ' input-focus';
      });
      document.querySelectorAll('[style*="submitButton"]').forEach(el => {
        el.className = (el.className || '') + ' submit-btn-hover';
      });
      document.querySelectorAll('[style*="cancelButton"]').forEach(el => {
        el.className = (el.className || '') + ' cancel-btn-hover';
      });
      document.querySelectorAll('[style*="editButton"]').forEach(el => {
        el.className = (el.className || '') + ' edit-btn-hover';
      });
      document.querySelectorAll('[style*="deleteButton"]').forEach(el => {
        el.className = (el.className || '') + ' delete-btn-hover';
      });
      document.querySelectorAll('[style*="contactLink"]').forEach(el => {
        el.className = (el.className || '') + ' contact-link-hover';
      });
    }, 200);
  }
};

// تشغيل إضافة classes عند التحميل
if (typeof window !== 'undefined') {
  addHoverClasses();
}